import { RootState } from "lib/redux/root/redux.types";
import { createSelector } from "@reduxjs/toolkit";
// import { useAppSelector } from "lib/redux/hooks";
// import { useSelector } from "react-redux";

// === Appointments State Selectors ===
//
// Selector functions that accept Redux state as an argument and return data derived
// from that state. Selectors can provide performance optimizations (e.g. using the
// "reselect" library) and can also encapsulate access to the global state tree.
//
// Use these functions to avoid having to do:
//   state.appointments.init ...
// calls everywhere in the code
//

///////selectAppointmentById won't work as it takes in additional param
// export const selectAppointmentById = (
//   rootState: RootState,
//   id: string
// ) => {
//   return selectAppointmentsLookup(rootState)[id];
// };

// === Base selectors ===
export const selectAppointments = (rootState: RootState) =>
  rootState.appointments;

// === Memoized selectors ===
// These selectors use createSelector for memoization to prevent unnecessary re-renders

// Selector for bundled appointment data needed in AvailableBundledSlotsSection
export const selectBundledAppointmentData = createSelector(
  [
    (state: RootState) =>
      state.appointments.rescheduleLinkedAppointment.selectedLinkedAppointment,
    (state: RootState) => state.appointments.appointmentMeta.isHsgAppt,
    (state: RootState) => state.appointments.appointmentMeta.visitTypeId,
    (state: RootState) => state.appointments.appointmentMeta.institutionCode,
  ],
  (selectedLinkedAppointment, isHsgAppt, visitTypeId, institutionCode) => {
    if (selectedLinkedAppointment) {
      // rescheduling a selected linked appointment
      const linkedAppointment = selectedLinkedAppointment;
      const parentVisitToSearchParam = [linkedAppointment.slotId];

      // Flow for multiple linked appointments
      if (linkedAppointment.linkedAppointments) {
        const childrenVisitToSearchParams =
          linkedAppointment.linkedAppointments?.map((appt) => {
            if (appt.slotId) {
              return appt.slotId;
            } else {
              return null;
            }
          });

        const linkedApptVisitTypeId = linkedAppointment.linkedAppointments?.map(
          (appt) => {
            if (appt.visitTypeId) {
              return appt.visitTypeId;
            } else {
              return null;
            }
          },
        );

        return {
          visitTypeId: linkedAppointment.visitTypeId,
          linkedApptVisitTypeId: linkedApptVisitTypeId,
          institutionCode: linkedAppointment.institutionCode,
          slotIdList: [
            ...parentVisitToSearchParam,
            ...childrenVisitToSearchParams,
          ],
          isBundledLinkedAppointments: true,
        };
      } else {
        // Flow for single linked appointment
        return {
          visitTypeId: null,
          linkedApptVisitTypeId: null,
          institutionCode: linkedAppointment.institutionCode,
          slotIdList: [...parentVisitToSearchParam],
          isBundledLinkedAppointments: true,
        };
      }
    } else if (isHsgAppt) {
      return {
        visitTypeId: visitTypeId,
        linkedApptVisitTypeId: null,
        institutionCode: institutionCode,
        slotIdList: null,
        isBundledLinkedAppointments: true,
      };
    }
    return {
      visitTypeId: visitTypeId,
      linkedApptVisitTypeId: null,
      institutionCode: institutionCode,
      slotIdList: null,
      isBundledLinkedAppointments: false,
    };
  },
);

export const selectAppointmentsLookup = (rootState: RootState) =>
  rootState.appointments.init.lookup;

export const selectAppointmentsInit = (rootState: RootState) =>
  rootState.appointments.init;

export const selectAppointmentsSystemSettings = (rootState: RootState) =>
  rootState.appointments.systemSettings;

export const selectAppointmentsAnnouncements = (rootState: RootState) =>
  rootState.appointments.announcements;

export const selectAppointmentMeta = (rootState: RootState) =>
  rootState.appointments.appointmentMeta;

export const selectAppointmentTriage = (rootState: RootState) =>
  rootState.appointments.triage;

export const selectAppointmentMammogram = (rootState: RootState) =>
  rootState.appointments.mammogram;

export const selectAppointmentDocByType = (rootState: RootState) =>
  rootState.appointments.documentByType;

export const selectCheckInSuccess = (rootState: RootState) =>
  rootState.appointments.checkInSuccess;

export const selectApptDetail = (state: RootState) => {
  const apptId = selectApptId(state);
  return state.appointments.register[apptId];
};

export const selectApptId = (rootState: RootState) =>
  rootState.appointments.appointmentMeta.appointmentId || "";

export const selectPersistDetail = (state: RootState) => {
  const apptId = selectApptId(state);
  return state.appointments.persist[apptId];
};

export const selectPopHealthStatus = (rootState: RootState) =>
  rootState.appointments.popHealthStatus;

export const selectRescheduleLinkedAppointments = (state: RootState) =>
  state.appointments.rescheduleLinkedAppointment;

export const selectCreateAppointment = (state: RootState) =>
  state.appointments.create;

export const selectCreateTcuAppointment = (state: RootState) =>
  state.appointments.create.createTcuAppointment;

export const selectKioskQuestionnaire = (state: RootState) =>
  state.appointments.kioskQuestionnaire;
